import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
	CONSENT_RESET_EVENT,
	denyConsent,
	grantConsent,
	isConfigured,
	readConsent,
	restoreConsent,
	trackPageView
} from "./analytics";

/**
 * ============================================================================
 * The consent banner, and the only thing that can turn analytics on. D-104.
 * ============================================================================
 *
 * IT RENDERS NOTHING ON THE SERVER, AND THAT IS A CORRECTNESS RULE RATHER THAN
 * AN OPTIMISATION. Whether to show it depends on `localStorage`, which does not
 * exist at pre-render time — so a component that read it during the first
 * render would emit one thing into the HTML and a different thing on hydration.
 * React 18 answers that by throwing away the server markup and re-rendering the
 * whole root, which is the cost the whole SSG migration exists to avoid (D-042),
 * and it would be paid on every page of the site. So the component starts in
 * the pre-rendered state — invisible — and flips in an effect after mount,
 * which is exactly the pattern D-100 settled for the achievements facade.
 *
 * IT ALSO RENDERS NOTHING WHEN THE BUILD CARRIES NO MEASUREMENT ID. Asking a
 * visitor to consent to analytics that cannot run would be a dark pattern in
 * reverse: friction bought for nothing. Every gate in this repository runs
 * against exactly that build, so no sweep has to know this component exists.
 *
 * THE PAGE-VIEW EFFECT IS SEPARATE FROM THE BANNER EFFECT, deliberately. This
 * is a single-page app: `gtag.js` sends one automatic page view for the
 * document it loaded in, and every client-side route change after that would go
 * uncounted. `config` therefore sets `send_page_view: false` and the counting
 * happens here, on `location.pathname`, which is the one value that changes on
 * every navigation the router performs.
 *
 * WHAT THE COPY SAYS AND WHY IT SAYS IT. It names Google Analytics, says what
 * is collected in the terms a reader recognises, and does not claim the site
 * can delete a cookie already set on a Google domain — because it cannot, and a
 * control that looks like one and is none is worse than none (the same argument
 * `analytics.ts` makes about `anonymize_ip`). Decline is a real button of equal
 * weight, not a link hidden under an "accept all": consent that is easier to
 * give than to refuse is not consent under the DPDPA or the GDPR.
 */
const ConsentBanner = (): JSX.Element | null => {
	const [visible, setVisible] = useState(false);
	const panelRef = useRef<HTMLDivElement | null>(null);
	const location = useLocation();

	/* Mount effect: decide whether to ask, and restore an earlier accept. */
	useEffect(() => {
		if (!isConfigured()) return;
		const state = readConsent();
		if (state === "granted") restoreConsent(location.pathname);
		else if (state === "unset") setVisible(true);
		/* Deliberately mount-only. Re-running this on every navigation would
		   re-show the banner mid-session for a visitor who has not answered it,
		   and `restoreConsent` would inject a second page view beside the one
		   the route effect below already sends. */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* The footer's withdrawal control clears the stored choice and fires this;
	   the banner comes back so the visitor can answer again. Registered even
	   when analytics is unconfigured — the control is not rendered then, so
	   nothing can fire it, and a conditional listener would be a second place
	   for the two components to disagree about whether the feature exists. */
	useEffect(() => {
		const reopen = (): void => setVisible(true);
		window.addEventListener(CONSENT_RESET_EVENT, reopen);
		return () => window.removeEventListener(CONSENT_RESET_EVENT, reopen);
	}, []);

	/* Route effect: one page view per navigation. `trackPageView` is a no-op
	   until the tag has been injected, so this is safe before any consent. */
	useEffect(() => {
		trackPageView(location.pathname);
	}, [location.pathname]);

	/* THE BANNER MAKES ROOM FOR ITSELF. A fixed bar is out of flow, so without
	   this the bottom ~214px of every page (at 768) sits underneath it and a
	   visitor who has not answered cannot see the end of the page.

	   THIS WAS FIRST ADDED FOR THE WRONG REASON AND THE NOTE SAYS SO. The AA
	   sweep reported two contrast failures on Home that appeared only on a
	   configured build, and the hypothesis was that the sweep was reading the
	   banner's face as the ground for content behind it. Adding the padding
	   changed nothing, which killed that hypothesis; sampling the painted pixels
	   settled it instead — both elements paint white on the navy CTA gradient at
	   rgb(37,72,135), about 8.9:1, and the sweep's rgb(231,231,229) is the page
	   background, not their ground. The failures are the instrument (see
	   docs/TODO.md), and the banner did not cause them. It only changed the page
	   height, which moves `aa.mjs`'s scroll grid and brought those two elements
	   into the measured set for the first time — D-083's lesson exactly.

	   The padding is kept because reserving space for a fixed bar is correct on
	   its own terms, not because it fixed anything. It is MEASURED rather than
	   hardcoded, since the copy wraps to a different number of lines by width,
	   applied to `body` so it covers whatever the route renders, and removed on
	   dismissal so a visitor who answers gets the space back at once. */
	useEffect(() => {
		if (!visible) return undefined;
		const node = panelRef.current;
		if (!node) return undefined;
		const apply = (): void => {
			document.body.style.paddingBottom = `${node.getBoundingClientRect().height}px`;
		};
		apply();
		const observer = new ResizeObserver(apply);
		observer.observe(node);
		window.addEventListener("resize", apply);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", apply);
			document.body.style.paddingBottom = "";
		};
	}, [visible]);

	if (!visible) return null;

	const accept = (): void => {
		grantConsent(location.pathname);
		setVisible(false);
	};

	const decline = (): void => {
		denyConsent();
		setVisible(false);
	};

	return (
		<div
			role="dialog"
			aria-modal="false"
			aria-labelledby="consent-heading"
			ref={panelRef}
			className="fixed inset-x-0 bottom-0 z-[120] px-3 pb-3 sm:px-5 sm:pb-5"
		>
			<div className="card-shell mx-auto max-w-3xl">
				<div className="card-face flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-6">
					<div className="flex-1">
						<p
							id="consent-heading"
							className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--text-strong)]"
						>
							Analytics
						</p>
						<p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
							This site uses Google Analytics to count visits and see which pages and referrers bring
							people here. It records pages viewed, referrer, country and device — never your name or
							anything that identifies you personally. Nothing is sent to Google unless you accept.
						</p>
					</div>
					<div className="flex shrink-0 gap-3">
						<button
							type="button"
							onClick={decline}
							className="rounded-[10px] border border-[var(--border-pill)] bg-[var(--surface)] px-4 py-2.5 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-strong)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
						>
							Decline
						</button>
						<button
							type="button"
							onClick={accept}
							className="rounded-[10px] border border-[var(--cat-violet-border)] bg-[var(--cat-violet-fill)] px-4 py-2.5 text-sm font-semibold text-[var(--cat-violet-text)] transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
						>
							Accept
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ConsentBanner;
